import {
  HealthResponse,
  HealthResponseSchema,
  CurrentActorContext,
  CurrentActorContextResponseSchema,
  ReadinessResponse,
  ReadinessResponseSchema,
  DashboardActivityResponseSchema,
  DashboardAttentionResponseSchema,
  DashboardSummaryResponseSchema,
  CatalogImportResponseSchema,
  CatalogImportListResponseSchema,
  CatalogImportSummaryResponseSchema,
  CatalogSnapshotResponseSchema,
  type CatalogImportSummary,
  type CatalogImportListItem,
  type DashboardActivityItem,
  type DashboardAttentionItem,
  type DashboardSummary,
  LoyaltyAccountResponseSchema,
  LoyaltyCommandResponseSchema,
  LoyaltyEligibleCustomersResponseSchema,
  LoyaltyHistoryResponseSchema,
  LoyaltyTiersResponseSchema,
  type LoyaltyAccountSummary,
  type LoyaltyEligibleCustomer,
  type LoyaltyLedgerHistoryItem,
  type LoyaltyTier,
  type ReportingPeriod,
  CustomerJourneyResponseSchema,
  OpenIdentityConflictResponseSchema,
  UnhandledConversationResponseSchema,
  type CustomerJourneyItem,
  type OpenIdentityConflict,
  type UnhandledConversation,
  AutomationCommandResponseSchema,
  AutomationRulesResponseSchema,
  AutomationRunsResponseSchema,
  AutomationTriggerEventsResponseSchema,
  type AutomationCreateRule,
  type AutomationRule,
  type AutomationRun,
  type AutomationTriggerEvent,
  AnalyticsOverviewResponseSchema,
  type AnalyticsOverview,
  ProviderActivityResponseSchema,
  type ProviderActivityItem,
} from '@raptor/contracts';

export interface ApiClientConfig {
  baseUrl?: string;
  accessTokenProvider?: AccessTokenProvider;
}

export type AccessTokenProvider = () => string | null;

export class AuthenticationRequiredError extends Error {
  public constructor() {
    super('An authenticated session is required for this request.');
    this.name = 'AuthenticationRequiredError';
  }
}

export class ApiResponseError extends Error {
  public constructor(
    public readonly status: number,
    message = 'The workspace context could not be loaded.',
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiResponseError';
  }
}

/**
 * Normalizes and validates an API base URL:
 * - Empty string, undefined, or whitespace resolves to '' (for local Vite dev proxy mode)
 * - Absolute configured values must be valid URLs with http: or https: schemes
 * - Strips trailing slash(es)
 * - Throws descriptive Error on malformed URLs or unsupported protocols
 */
export function normalizeApiBaseUrl(rawUrl?: string): string {
  if (!rawUrl) {
    return '';
  }
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return '';
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(trimmed);
  } catch {
    throw new Error(
      `Invalid API base URL: "${trimmed}". Must be a valid absolute URL with http: or https: scheme.`
    );
  }

  if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
    throw new Error(
      `Invalid API base URL protocol: "${parsedUrl.protocol}". Only "http:" and "https:" schemes are supported.`
    );
  }

  return trimmed.replace(/\/+$/, '');
}

/**
 * Resolves default API base URL from Vite environment:
 * - In browser/Vite: reads import.meta.env.VITE_API_BASE_URL
 * - Unset/empty/whitespace: returns '' (uses local relative path with Vite proxy)
 * - Configured invalid URL or unsupported scheme: throws immediately (fails fast)
 * - Fallback environment without import.meta.env: returns ''
 */
export function resolveDefaultApiBaseUrl(): string {
  let rawEnvUrl: string | undefined;
  try {
    if (
      typeof import.meta !== 'undefined' &&
      import.meta.env &&
      typeof import.meta.env.VITE_API_BASE_URL === 'string'
    ) {
      rawEnvUrl = import.meta.env.VITE_API_BASE_URL;
    }
  } catch {
    // Environment does not support import.meta.env access
  }

  return normalizeApiBaseUrl(rawEnvUrl);
}

export class ApiClient {
  private readonly baseUrl: string;
  private accessTokenProvider?: AccessTokenProvider;

  constructor(config: ApiClientConfig = {}) {
    this.accessTokenProvider = config.accessTokenProvider;
    if (config.baseUrl !== undefined) {
      this.baseUrl = normalizeApiBaseUrl(config.baseUrl);
    } else {
      this.baseUrl = resolveDefaultApiBaseUrl();
    }
  }

  public getBaseUrl(): string {
    return this.baseUrl;
  }

  public setAccessTokenProvider(provider: AccessTokenProvider | undefined): void {
    this.accessTokenProvider = provider;
  }

  public buildUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${cleanPath}`;
  }

  public async getHealth(): Promise<HealthResponse> {
    const url = this.buildUrl('/api/v1/health');
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Health check failed with HTTP ${res.status}`);
    }
    const data = await res.json();
    return HealthResponseSchema.parse(data);
  }

  /**
   * Transport helper for future protected endpoints. The token is resolved at
   * request time so refresh and logout cannot leave a captured stale token.
   */
  public async fetchAuthenticated(path: string, init: RequestInit = {}): Promise<Response> {
    const accessToken = this.accessTokenProvider?.();
    if (!accessToken) throw new AuthenticationRequiredError();

    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${accessToken}`);
    return fetch(this.buildUrl(path), { ...init, headers });
  }

  public async getReadiness(): Promise<ReadinessResponse> {
    const url = this.buildUrl('/api/v1/readiness');
    const res = await fetch(url);
    const data = await res.json();
    return ReadinessResponseSchema.parse(data);
  }

  public async getCurrentActorContext(): Promise<CurrentActorContext> {
    const response = await this.fetchAuthenticated('/api/v1/me');
    if (!response.ok) {
      if (response.status === 401) {
        throw new ApiResponseError(401, 'Your session is no longer usable.');
      }
      if (response.status === 403) {
        throw new ApiResponseError(403, 'Workspace access is unavailable.');
      }
      throw new ApiResponseError(response.status);
    }

    return CurrentActorContextResponseSchema.parse(await response.json()).data;
  }

  public async getCrmCustomers(organizationId: string): Promise<Array<{ id: string; displayName: string; status: string }>> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/customers?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Customers could not be loaded.');
    return (await response.json()).data;
  }

  public async getCrmCustomerDetail(id: string): Promise<{ id: string; displayName: string; status: string; originBranchId: string; createdAt: string; contacts: Array<{ id: string; type: string; value: string }> }> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/customers/${id}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Customer details could not be loaded.');
    return (await response.json()).data;
  }

  public async createCrmCustomer(input: { organizationId: string; branchId: string; displayName: string; contactType: 'PHONE' | 'WHATSAPP' | 'EMAIL'; contactValue: string }): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated('/api/v1/crm/customers', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new ApiResponseError(response.status, 'Customer could not be saved.', payload?.error?.details);
    }
    return (await response.json()).data;
  }

  public async findCrmDuplicateCustomer(input: { organizationId: string; branchId: string; contactType: 'PHONE' | 'WHATSAPP' | 'EMAIL'; contactValue: string }): Promise<{ id: string; displayName: string } | null> {
    const search = new URLSearchParams(input).toString();
    const response = await this.fetchAuthenticated(`/api/v1/crm/customers/duplicate-candidate?${search}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Existing customer lookup could not be completed.');
    return (await response.json()).data;
  }

  public async getCrmLeads(organizationId: string): Promise<Array<{ id: string; customerId: string; status: string; assigneeMembershipId: string | null }>> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/leads?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Leads could not be loaded.');
    return (await response.json()).data;
  }

  public async getCrmLeadSources(organizationId: string): Promise<Array<{ id: string; name: string }>> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/lead-sources?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Lead sources could not be loaded.');
    return (await response.json()).data;
  }

  public async createCrmLeadSource(input: { organizationId: string; name: string }): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated('/api/v1/crm/lead-sources', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) throw new ApiResponseError(response.status, 'Lead source could not be saved.');
    return (await response.json()).data;
  }

  public async createCrmLead(input: { organizationId: string; branchId: string; customerId: string; sourceId: string; assigneeMembershipId: string }): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated('/api/v1/crm/leads', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
    if (!response.ok) throw new ApiResponseError(response.status, 'Lead could not be saved.');
    return (await response.json()).data;
  }

  public async changeCrmLeadStatus(id: string, status: 'NEW' | 'CONTACTED' | 'FOLLOW_UP' | 'QUALIFIED' | 'WON' | 'LOST'): Promise<void> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/leads/${id}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
    if (!response.ok) throw new ApiResponseError(response.status, 'Lead status could not be updated.');
  }

  public async getCrmTasks(organizationId: string): Promise<Array<{ id: string; title: string; status: string; customerId: string | null; leadId: string | null; assigneeMembershipId: string }>> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/tasks?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Tasks could not be loaded.');
    return (await response.json()).data;
  }

  public async createCrmTask(input: { organizationId: string; branchId: string; customerId?: string; leadId?: string; assigneeMembershipId: string; title: string }): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated('/api/v1/crm/tasks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
    if (!response.ok) throw new ApiResponseError(response.status, 'Follow-up could not be saved.');
    return (await response.json()).data;
  }

  public async completeCrmTask(id: string): Promise<void> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/tasks/${id}/complete`, { method: 'PATCH' });
    if (!response.ok) throw new ApiResponseError(response.status, 'Task could not be completed.');
  }

  public async getCrmCustomerTimeline(customerId: string): Promise<Array<{ id: string; eventType: string; occurredAt: string }>> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/customers/${customerId}/timeline`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Customer history could not be loaded.');
    return (await response.json()).data;
  }

  public async getCrmBranchAssignees(branchId: string): Promise<Array<{ membershipId: string; displayName: string | null; role: string }>> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/branches/${branchId}/assignees`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Eligible assignees could not be loaded.');
    return (await response.json()).data;
  }

  public async assignCrmLead(id: string, assigneeMembershipId: string): Promise<void> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/leads/${id}/assignment`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ assigneeMembershipId }) });
    if (!response.ok) throw new ApiResponseError(response.status, 'Lead could not be reassigned.');
  }

  public async assignCrmTask(id: string, assigneeMembershipId: string): Promise<void> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/tasks/${id}/assignment`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ assigneeMembershipId }) });
    if (!response.ok) throw new ApiResponseError(response.status, 'Task could not be reassigned.');
  }

  public async reopenCrmTask(id: string): Promise<void> {
    const response = await this.fetchAuthenticated(`/api/v1/crm/tasks/${id}/reopen`, { method: 'PATCH' });
    if (!response.ok) throw new ApiResponseError(response.status, 'Task could not be reopened.');
  }

  public async getDashboardSummary(organizationId: string, period: ReportingPeriod): Promise<DashboardSummary> {
    const response = await this.fetchAuthenticated(`/api/v1/dashboard/summary?${new URLSearchParams({ organizationId, period })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'The dashboard could not be loaded.');
    return DashboardSummaryResponseSchema.parse(await response.json()).data;
  }

  public async getDashboardAttention(organizationId: string): Promise<DashboardAttentionItem[]> {
    const response = await this.fetchAuthenticated(`/api/v1/dashboard/attention?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Attention items could not be loaded.');
    return DashboardAttentionResponseSchema.parse(await response.json()).data;
  }

  public async getDashboardActivity(organizationId: string, period: ReportingPeriod): Promise<DashboardActivityItem[]> {
    const response = await this.fetchAuthenticated(`/api/v1/dashboard/activity?${new URLSearchParams({ organizationId, period })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Recent activity could not be loaded.');
    return DashboardActivityResponseSchema.parse(await response.json()).data;
  }

  public async getAnalyticsOverview(organizationId: string, period: ReportingPeriod): Promise<AnalyticsOverview> {
    const response = await this.fetchAuthenticated(`/api/v1/analytics/overview?${new URLSearchParams({ organizationId, period })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Analytics could not be loaded.');
    return AnalyticsOverviewResponseSchema.parse(await response.json()).data;
  }

  public async getUnhandledConversations(organizationId: string): Promise<UnhandledConversation[]> {
    const response = await this.fetchAuthenticated(`/api/v1/communications/unhandled?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Unhandled conversations could not be loaded.');
    return UnhandledConversationResponseSchema.parse(await response.json()).data;
  }

  public async markConversationHandled(conversationId: string): Promise<void> {
    const response = await this.fetchAuthenticated(`/api/v1/communications/conversations/${conversationId}/status`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'HANDLED' }),
    });
    if (!response.ok) throw new ApiResponseError(response.status, 'Conversation could not be marked handled.');
  }

  public async getOpenIdentityConflicts(organizationId: string): Promise<OpenIdentityConflict[]> {
    const response = await this.fetchAuthenticated(`/api/v1/communications/identity-conflicts?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Identity reviews could not be loaded.');
    return OpenIdentityConflictResponseSchema.parse(await response.json()).data;
  }

  public async reviewIdentityConflict(conflictId: string, input: { customerId?: string; reject?: boolean }): Promise<void> {
    const response = await this.fetchAuthenticated(`/api/v1/communications/identity-conflicts/${conflictId}/review`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    });
    if (!response.ok) throw new ApiResponseError(response.status, 'Identity review could not be saved.');
  }

  public async getCustomerJourney(customerId: string): Promise<CustomerJourneyItem[]> {
    const response = await this.fetchAuthenticated(`/api/v1/journeys/customers/${customerId}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Customer journey could not be loaded.');
    return CustomerJourneyResponseSchema.parse(await response.json()).data;
  }

  public async getProviderActivity(organizationId: string): Promise<ProviderActivityItem[]> {
    const response = await this.fetchAuthenticated(`/api/v1/integrations/activity?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Channel activity could not be loaded.');
    return ProviderActivityResponseSchema.parse(await response.json()).data;
  }

  public async getAutomationRules(organizationId: string): Promise<AutomationRule[]> {
    const response = await this.fetchAuthenticated(`/api/v1/automation/rules?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Automation rules could not be loaded.');
    return AutomationRulesResponseSchema.parse(await response.json()).data;
  }

  public async getAutomationRuns(organizationId: string): Promise<AutomationRun[]> {
    const response = await this.fetchAuthenticated(`/api/v1/automation/runs?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Automation history could not be loaded.');
    return AutomationRunsResponseSchema.parse(await response.json()).data;
  }

  public async getAutomationTriggerEvents(organizationId: string): Promise<AutomationTriggerEvent[]> {
    const response = await this.fetchAuthenticated(`/api/v1/automation/trigger-events?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Eligible automation activity could not be loaded.');
    return AutomationTriggerEventsResponseSchema.parse(await response.json()).data;
  }

  public async createAutomationRule(input: AutomationCreateRule): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated('/api/v1/automation/rules', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    });
    if (!response.ok) throw new ApiResponseError(response.status, 'Automation rule could not be created.');
    return AutomationCommandResponseSchema.parse(await response.json()).data;
  }

  public async setAutomationRuleEnabled(ruleId: string, isEnabled: boolean): Promise<void> {
    const response = await this.fetchAuthenticated(`/api/v1/automation/rules/${ruleId}/enabled`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ isEnabled }),
    });
    if (!response.ok) throw new ApiResponseError(response.status, 'Automation rule could not be updated.');
  }

  public async executeAutomationRule(ruleId: string, triggerTimelineEventId: string): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated(`/api/v1/automation/rules/${ruleId}/execute`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ triggerTimelineEventId }),
    });
    if (!response.ok) throw new ApiResponseError(response.status, 'Automation could not be run for that activity.');
    return AutomationCommandResponseSchema.parse(await response.json()).data;
  }

  public async retryAutomationRun(runId: string): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated(`/api/v1/automation/runs/${runId}/retry`, { method: 'POST' });
    if (!response.ok) throw new ApiResponseError(response.status, 'Automation run could not be retried.');
    return AutomationCommandResponseSchema.parse(await response.json()).data;
  }
  public async getLoyaltyAccount(customerId: string): Promise<LoyaltyAccountSummary> {
    const response = await this.fetchAuthenticated(`/api/v1/loyalty/customers/${customerId}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Loyalty account could not be loaded.');
    return LoyaltyAccountResponseSchema.parse(await response.json()).data;
  }

  public async getLoyaltyCustomers(organizationId: string): Promise<LoyaltyEligibleCustomer[]> {
    const response = await this.fetchAuthenticated(`/api/v1/loyalty/customers?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Loyalty customers could not be loaded.');
    return LoyaltyEligibleCustomersResponseSchema.parse(await response.json()).data;
  }

  public async getLoyaltyHistory(customerId: string): Promise<LoyaltyLedgerHistoryItem[]> {
    const response = await this.fetchAuthenticated(`/api/v1/loyalty/customers/${customerId}/history`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Loyalty history could not be loaded.');
    return LoyaltyHistoryResponseSchema.parse(await response.json()).data;
  }

  public async applyLoyaltyPoints(kind: 'earn' | 'redeem', input: {
    organizationId: string; branchId: string; customerId: string; points: number; idempotencyKey: string; note?: string;
  }): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated(`/api/v1/loyalty/${kind}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    });
    if (!response.ok) throw new ApiResponseError(response.status, `Loyalty points could not be ${kind === 'earn' ? 'earned' : 'redeemed'}.`);
    return LoyaltyCommandResponseSchema.parse(await response.json()).data;
  }

  public async getLoyaltyTiers(organizationId: string): Promise<LoyaltyTier[]> {
    const response = await this.fetchAuthenticated(`/api/v1/loyalty/tiers?${new URLSearchParams({ organizationId })}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Loyalty tiers could not be loaded.');
    return LoyaltyTiersResponseSchema.parse(await response.json()).data;
  }

  public async createLoyaltyTier(input: { organizationId: string; name: string; minimumPoints: number }): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated('/api/v1/loyalty/tiers', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    });
    if (!response.ok) throw new ApiResponseError(response.status, 'Loyalty tier could not be created.');
    return LoyaltyCommandResponseSchema.parse(await response.json()).data;
  }

  public async assignLoyaltyTier(customerId: string, tierId: string): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated(`/api/v1/loyalty/customers/${customerId}/tier`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tierId }),
    });
    if (!response.ok) throw new ApiResponseError(response.status, 'Loyalty tier could not be assigned.');
    return LoyaltyCommandResponseSchema.parse(await response.json()).data;
  }
  public async createCatalogImport(input: { organizationId: string; branchId: string; sourceKind: 'CSV_FILE' | 'XLSX_FILE' | 'BCONNECT_SYNTHETIC_WORKBOOK'; sourceFilename: string; contentSha256: string; sourceBytes: number; parserProvenance: Record<string, unknown> }): Promise<{ id: string }> {
    const response = await this.fetchAuthenticated('/api/v1/catalog/imports', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
    if (!response.ok) throw new ApiResponseError(response.status, 'Catalog import could not be registered.');
    return CatalogImportResponseSchema.parse(await response.json()).data;
  }
  public async getCatalogImports(organizationId: string, branchId: string): Promise<CatalogImportListItem[]> {
    const response = await this.fetchAuthenticated(`/api/v1/catalog/imports?organizationId=${encodeURIComponent(organizationId)}&branchId=${encodeURIComponent(branchId)}`);
    if (!response.ok) throw new ApiResponseError(response.status, 'Catalog imports could not be loaded.');
    return CatalogImportListResponseSchema.parse(await response.json()).data;
  }
  public async registerCatalogSheet(importId: string, input: { sheetName: string; sheetIndex: number; rowCount: number; columnCount: number; headerCandidates: number[] }): Promise<void> { const response = await this.fetchAuthenticated(`/api/v1/catalog/imports/${importId}/sheets`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) }); if (!response.ok) throw new ApiResponseError(response.status, 'Workbook sheet could not be registered.'); }
  public async selectCatalogSheet(importId: string, input: { sheetName: string; headerRowNumber: number; headerDetection: Record<string, unknown> }): Promise<void> { const response = await this.fetchAuthenticated(`/api/v1/catalog/imports/${importId}/sheet-selection`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) }); if (!response.ok) throw new ApiResponseError(response.status, 'Workbook header could not be selected.'); }
  public async saveCatalogMapping(importId: string, mappings: Array<{ targetField: string; sourceHeader: string; sourceColumnIndex: number; normalization: Record<string, unknown> }>): Promise<void> { const response = await this.fetchAuthenticated(`/api/v1/catalog/imports/${importId}/mapping`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mappings }) }); if (!response.ok) throw new ApiResponseError(response.status, 'Column mapping could not be saved.'); }
  public async stageCatalogRows(importId: string, rows: Array<{ rowNumber: number; rawValues: Record<string, unknown> }>): Promise<void> { const response = await this.fetchAuthenticated(`/api/v1/catalog/imports/${importId}/rows`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rows }) }); if (!response.ok) throw new ApiResponseError(response.status, 'Catalog rows could not be validated.'); }
  public async profileCatalogImport(importId: string, columnProfile: Record<string, unknown>): Promise<void> { const response = await this.fetchAuthenticated(`/api/v1/catalog/imports/${importId}/profile`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ columnProfile }) }); if (!response.ok) throw new ApiResponseError(response.status, 'Catalog import could not be profiled.'); }
  public async getCatalogImport(importId: string): Promise<CatalogImportSummary> { const response = await this.fetchAuthenticated(`/api/v1/catalog/imports/${importId}`); if (!response.ok) throw new ApiResponseError(response.status, 'Catalog import could not be loaded.'); return CatalogImportSummaryResponseSchema.parse(await response.json()).data; }
  public async approveCatalogImport(importId: string): Promise<{ snapshotId: string }> { const response = await this.fetchAuthenticated(`/api/v1/catalog/imports/${importId}/approve`, { method: 'POST' }); if (!response.ok) throw new ApiResponseError(response.status, 'Catalog approval was denied.'); return CatalogSnapshotResponseSchema.parse(await response.json()).data; }
  public async retryCatalogImport(importId: string): Promise<{ id: string }> { const response = await this.fetchAuthenticated(`/api/v1/catalog/imports/${importId}/retry`, { method: 'POST' }); if (!response.ok) throw new ApiResponseError(response.status, 'Catalog retry was denied.'); return CatalogImportResponseSchema.parse(await response.json()).data; }
  private async ct<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
    const response = await this.fetchAuthenticated(path, body === undefined ? { method } : { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!response.ok) throw new ApiResponseError(response.status, 'The relationship record could not be saved.');
    if (response.status === 204) return undefined as T;
    return (await response.json()).data as T;
  }
  public getCorporateCompanies() { return this.ct<Array<{ id: string; legalName: string; status: string; branchId: string; assigneeMembershipId: string | null }>>('/api/v1/corporate/companies'); }
  public createCorporateCompany(input: { organizationId: string; branchId: string; legalName: string; assigneeMembershipId?: string | null }) { return this.ct<{ id: string }>('/api/v1/corporate/companies', 'POST', input); }
  public getCorporateContacts(companyId: string) { return this.ct<Array<{ id: string; displayName: string; title: string | null; contactType: string; contactValue: string; isPrimary: boolean }>>(`/api/v1/corporate/companies/${companyId}/contacts`); }
  public addCorporateContact(companyId: string, input: { displayName: string; title?: string; contactType: 'PHONE' | 'WHATSAPP' | 'EMAIL'; contactValue: string; isPrimary?: boolean }) { return this.ct<{ id: string }>(`/api/v1/corporate/companies/${companyId}/contacts`, 'POST', input); }
  public getCorporateLeads(companyId: string) { return this.ct<Array<{ id: string; contactId: string | null; title: string; status: string; assigneeMembershipId: string | null }>>(`/api/v1/corporate/companies/${companyId}/leads`); }
  public createCorporateLead(companyId: string, input: { contactId?: string | null; title: string; assigneeMembershipId?: string | null }) { return this.ct<{ id: string }>(`/api/v1/corporate/companies/${companyId}/leads`, 'POST', input); }
  public getPipelineStages() { return this.ct<Array<{ id: string; stageKey: string; label: string; position: number; outcome: 'OPEN' | 'WON' | 'LOST'; isActive: boolean }>>('/api/v1/corporate/pipeline-stages'); }
  public createPipelineStage(input: { organizationId: string; stageKey: string; label: string; position: number; outcome?: 'OPEN' | 'WON' | 'LOST' }) { return this.ct<{ id: string }>('/api/v1/corporate/pipeline-stages', 'POST', input); }
  public getCorporateOpportunities(companyId: string) { return this.ct<Array<{ id: string; companyId: string; title: string; status: string; pipelineStageId: string; assigneeMembershipId: string | null }>>(`/api/v1/corporate/companies/${companyId}/opportunities`); }
  public createOpportunity(companyId: string, input: { contactId?: string | null; leadId?: string | null; pipelineStageId: string; title: string; assigneeMembershipId?: string | null }) { return this.ct<{ id: string }>(`/api/v1/corporate/companies/${companyId}/opportunities`, 'POST', input); }
  public moveOpportunity(id: string, pipelineStageId: string) { return this.ct<void>(`/api/v1/corporate/opportunities/${id}/stage`, 'PATCH', { pipelineStageId }); }
  public assignOpportunity(id: string, assigneeMembershipId: string) { return this.ct<void>(`/api/v1/corporate/opportunities/${id}/assignment`, 'PATCH', { assigneeMembershipId }); }
  public addCorporateNote(companyId: string, body: string) { return this.ct<{ id: string }>(`/api/v1/corporate/companies/${companyId}/notes`, 'POST', { body }); }
  public createCorporateTask(companyId: string, input: { assigneeMembershipId: string; title: string }) { return this.ct<{ id: string }>(`/api/v1/corporate/companies/${companyId}/tasks`, 'POST', input); }
  public completeCorporateTask(id: string) { return this.ct<void>(`/api/v1/corporate/tasks/${id}/complete`, 'PATCH'); }
  public getCorporateActivity(companyId: string) { return this.ct<{ notes: Array<{ id: string; body: string }>; tasks: Array<{ id: string; title: string; status: string }>; timeline: Array<{ id: string; eventType: string }> }>(`/api/v1/corporate/companies/${companyId}/activity`); }
  public getInsuranceProviders() { return this.ct<Array<{ id: string; name: string; status: string; branchId: string; assigneeMembershipId: string | null }>>('/api/v1/insurance/providers'); }
  public createInsuranceProvider(input: { organizationId: string; branchId: string; name: string; assigneeMembershipId?: string | null }) { return this.ct<{ id: string }>('/api/v1/insurance/providers', 'POST', input); }
  public getInsuranceContacts(providerId: string) { return this.ct<Array<{ id: string; displayName: string; title: string | null; contactType: string; contactValue: string }>>(`/api/v1/insurance/providers/${providerId}/contacts`); }
  public addInsuranceContact(providerId: string, input: { displayName: string; title?: string; contactType: 'PHONE' | 'WHATSAPP' | 'EMAIL'; contactValue: string; isPrimary?: boolean }) { return this.ct<{ id: string }>(`/api/v1/insurance/providers/${providerId}/contacts`, 'POST', input); }
  public setInsuranceProviderStatus(id: string, status: 'PROSPECT' | 'ACTIVE' | 'INACTIVE') { return this.ct<void>(`/api/v1/insurance/providers/${id}/status`, 'PATCH', { status }); }
  public assignInsuranceProvider(id: string, assigneeMembershipId: string) { return this.ct<void>(`/api/v1/insurance/providers/${id}/assignment`, 'PATCH', { assigneeMembershipId }); }
  public addInsuranceNote(providerId: string, body: string) { return this.ct<{ id: string }>(`/api/v1/insurance/providers/${providerId}/notes`, 'POST', { body }); }
  public getInsuranceFollowUps(providerId: string) { return this.ct<Array<{ id: string; providerId: string; title: string; status: string; assigneeMembershipId: string }>>(`/api/v1/insurance/providers/${providerId}/follow-ups`); }
  public createInsuranceFollowUp(providerId: string, input: { assigneeMembershipId: string; title: string }) { return this.ct<{ id: string }>(`/api/v1/insurance/providers/${providerId}/follow-ups`, 'POST', input); }
  public completeInsuranceFollowUp(id: string) { return this.ct<void>(`/api/v1/insurance/follow-ups/${id}/complete`, 'PATCH'); }
  public getInsuranceActivity(providerId: string) { return this.ct<{ notes: Array<{ id: string; body: string }>; timeline: Array<{ id: string; eventType: string }> }>(`/api/v1/insurance/providers/${providerId}/activity`); }
}

export const apiClient = new ApiClient();
